import {makeAutoObservable, runInAction} from 'mobx';
import {networkOptimization, createSSEConnection, streamPersistence} from '../../../lib/api';
import {
    GeographyFilterOptions,
    FetchGeoJsonResponse,
    FetchComplianceResponse,
    FetchProvidersResponse,
    FetchRunIdResponse,
    AppliedGeographyFilter, DISTANCE_CALC_TYPES,
    DisruptionAnalysisFilterOptions,
    AppliedDisruptionAnalysisFilter,
    IFetchReportsPayload,
    IGeographyFilterPayload,
    IDisruptionAnalysisFilterPayload
} from './types';
import Filter, {applyFilter} from '../../../lib/filter';
import auth from '../../../lib/auth';
import download from '../../../lib/download';
export default class NetworkOptimizationStore {

    static empty() {
        return new NetworkOptimizationStore();
    }

    // Store properties
    private _providerGeoJson: FetchGeoJsonResponse | null = null;
    private _reportProviderGeoJson: FetchGeoJsonResponse | null = null; // For report maps
    private _reportProviderGeoJsonError: string | null = null;
    private _reportProviderGeoJsonLoading = false;
    private _distanceCalcType: string = DISTANCE_CALC_TYPES[0].value;
    private _localProviders: FetchProvidersResponse = [];
    private _existingProviders: FetchProvidersResponse = [];
    private _selectedRosters: string[] = [];
    private _selectedRosterForComparison = '';
    private _providerRosters: any[] = [];
    private _loadingProviderRosters = false;

    private _patientRosters: any[] = [];
    private _loadingPatientRosters = false;

    // Track which streams we've already attempted to reconnect to
    private _reconnectedStreams: Set<string> = new Set();

    patientGeoJson: FetchGeoJsonResponse | null = null;
    appliedGeographyFilter: AppliedGeographyFilter = {
        state: '',
        county: '',
        standard_type: '',
        patient_roster_name: '',
        standard_version: ''
    };
    appliedDisruptionAnalysisFilter: AppliedDisruptionAnalysisFilter = {
        state: '',
        county: '',
        patient_roster_name: '',
        standard_type: '',
        zip: ''
    };
    availableGeographyFilters: Array<GeographyFilterOptions> = [];
    availableDisruptionAnalysisFilters: Array<DisruptionAnalysisFilterOptions> = [];
    specialtyFilter = '';
    disruptionAnalysisSpecialtyFilter = '';
    specialties: string[] = [];
    additionalProviderVisibleColumns: string[] = [];
    additionalProviderPendingColumns: string[] = [];
    rosterComparisonDisruptionAnalysisData: Array<any> | null = null;
    rosterComparisonNetworkAdequacyData: Array<any> | null = null;
    loadingRevisionRecalculation = false;
    isRosterActionLoading = false;
    loadingGeographyData = false;
    loadingDisruptionAnalysisData = false;
    loadingDisruptionAnalysisComparisonData = false;
    loadingNetworkAdequacyComparisonData = false;
    loadingStandardVersions = false;
    loadingSpecialtyData = false;
    loadingDisruptionAnalysisSpecialtyData = false;
    defaultComplianceScores: FetchComplianceResponse = [];
    complianceScores: FetchComplianceResponse = [];
    disruptionAnalysisComplianceScores: FetchComplianceResponse = [];
    runId: FetchRunIdResponse = null;
    error: string | null = null;
    navItems: {label: string; path: string, useRosterID: boolean, hideTab: boolean}[] = [];
    revisionId = 0;
    rosterRunCount = 0;
    rosterFileName = '';
    selectedAdditionalProviderKeys: string[] = [];
    deselectedExistingProviderKeys: string[] = [];
    existingProviderQueuedForDeletion: string | null = null;
    showRevertModal = false;
    activeTab = 0;
    filters: Filter[] = [];
    
    // Toast notification properties
    showToast = false;
    toastKind: 'success' | 'error' | 'info' | 'warning' = 'success';
    toastTitle = '';
    toastSubtitle = '';
    downloadingItems: {[itemName: string]: boolean} = {};

    constructor() {
        this.loadHeaders();
        makeAutoObservable(this);
    }

    // Check for existing streams in sessionStorage and attempt to reconnect
    // Call this method AFTER runId and revisionId are set
    checkForExistingStreams() {
        // Don't check if we're already recalculating or loading roster actions
        if (this.loadingRevisionRecalculation || this.isRosterActionLoading) {
            return;
        }

        // Clean up old streams first
        streamPersistence.cleanupOldStreams();

        const streams = streamPersistence.getAllStreams();

        Object.keys(streams).forEach(streamId => {
            // Skip if we've already reconnected to this stream
            if (this._reconnectedStreams.has(streamId)) {
                return;
            }

            const streamData = streams[streamId];

            // Handle different stream types
            if (streamData.streamType === 'recalculation') {
                // Only reconnect if the stream matches current context
                if (streamData.runId === this.runId && streamData.revisionId === this.revisionId) {
                    this._reconnectedStreams.add(streamId);
                    this.reconnectToRecalculationStream(streamId, streamData);
                }
            } else if (streamData.streamType === 'roster_copy') {
                // Reconnect to roster copy streams regardless of current context
                this._reconnectedStreams.add(streamId);
                this.reconnectToRosterCopyStream(streamId, streamData);
            }
        });
    }

    // Reconnect to an existing recalculation stream
    private async reconnectToRecalculationStream(streamId: string, metadata: any) {
        try {
            // First check if the stream still exists and its status
            const streamStatus = await networkOptimization.getStreamStatus(streamId);

            if (streamStatus.status === 'completed') {
                // Job already completed while page was closed
                runInAction(() => {
                    this.rosterRunCount += 1;
                    this.showToastNotification('success', 'Recalculation Complete', 'Network adequacy has been successfully updated.');
                });
                streamPersistence.removeStream(streamId);
                this._reconnectedStreams.delete(streamId);
                return;
            } else if (streamStatus.status === 'failed') {
                // Job failed while page was closed
                runInAction(() => {
                    this.showToastNotification('error', 'Recalculation Failed', streamStatus.error || 'Please try again or contact support.');
                });
                streamPersistence.removeStream(streamId);
                this._reconnectedStreams.delete(streamId);
                return;
            }

            // Stream still running - connect to it
            this.loadingRevisionRecalculation = true;
            this.showToastNotification('info', 'Reconnecting to recalculation', 'Resuming network adequacy calculation...');

            await createSSEConnection(
                streamId,
                (message: string) => {
                    // Show progress as toast notification
                    runInAction(() => {
                        this.showToastNotification('info', 'Recalculation Progress', message);
                    });
                },
                (status: 'COMPLETED' | 'FAILED') => {
                    if (status === 'COMPLETED') {
                        runInAction(() => {
                            this.rosterRunCount += 1;
                            this.hideToastNotification();
                            this.showToastNotification('success', 'Recalculation Complete', 'Network adequacy has been successfully updated.');
                            this.loadingRevisionRecalculation = false;
                        });
                    } else {
                        runInAction(() => {
                            this.hideToastNotification();
                            this.showToastNotification('error', 'Recalculation Failed', 'Please try again or contact support.');
                            this.loadingRevisionRecalculation = false;
                        });
                    }
                    // Clean up
                    streamPersistence.removeStream(streamId);
                    this._reconnectedStreams.delete(streamId);
                },
                (error) => {
                    console.error('SSE connection error:', error);
                    runInAction(() => {
                        this.loadingRevisionRecalculation = false;
                        this.hideToastNotification();
                        this.showToastNotification('error', 'Connection Error', 'Lost connection to recalculation. Please try again.');
                    });
                    // Clean up on error
                    streamPersistence.removeStream(streamId);
                    this._reconnectedStreams.delete(streamId);
                }
            );
        } catch (error) {
            // Stream not found or error checking status
            console.error('Error checking stream status:', error);
            runInAction(() => {
                this.loadingRevisionRecalculation = false;
                this.showToastNotification('error', 'Reconnection Failed', 'Could not reconnect to recalculation. Please try again.');
            });
            // Clean up the stale stream reference
            streamPersistence.removeStream(streamId);
            this._reconnectedStreams.delete(streamId);
        }
    }

    // Reconnect to an existing roster copy stream
    private async reconnectToRosterCopyStream(streamId: string, metadata: any) {
        try {
            // First check if the stream still exists and its status
            const streamStatus = await networkOptimization.getStreamStatus(streamId);

            // Check if we need to call updateRoster after copy completes (based on provider changes)
            const hasProviderChanges = (metadata.selectedAdditionalProviderKeys?.length > 0) ||
                                        (metadata.deselectedExistingProviderKeys?.length > 0);

            if (streamStatus.status === 'completed') {
                // Job already completed while page was closed
                runInAction(() => {
                    if (metadata.rosterId && metadata.revisionName) {
                        // Prevent duplicate: only add if not already present
                        const existingRoster = this._providerRosters.find(r => r.rosterId === metadata.rosterId.toString() && r.revisionName === metadata.revisionName);
                        let newRevisionId = null;
                        if (!existingRoster) {
                            newRevisionId = this.addNewRevisionToRoster(metadata.rosterId, metadata.revisionName, metadata.notes || '');
                        } else {
                            newRevisionId = existingRoster.revisionId;
                        }
                        // If there were provider changes, trigger updateRoster
                        if (hasProviderChanges && newRevisionId) {
                            // Clear roster action loading since copy is complete
                            this.isRosterActionLoading = false;
                            this.handlePostCopyUpdate(newRevisionId, metadata);
                        } else {
                            this.showToastNotification('success', 'Roster copied successfully', `${metadata.revisionName || 'Roster'} has been created`);
                            this.isRosterActionLoading = false;
                        }
                    }
                });
                streamPersistence.removeStream(streamId);
                this._reconnectedStreams.delete(streamId);
                return;
            } else if (streamStatus.status === 'failed') {
                // Job failed while page was closed
                runInAction(() => {
                    this.showToastNotification('error', 'Roster Copy Failed', streamStatus.error || 'Please try again or contact support.');
                    this.isRosterActionLoading = false;
                });
                streamPersistence.removeStream(streamId);
                this._reconnectedStreams.delete(streamId);
                return;
            }

            // Stream still running - connect to it
            this.isRosterActionLoading = true;
            this.showToastNotification('info', 'Reconnecting to roster copy', 'Resuming roster copy operation...');

            await createSSEConnection(
                streamId,
                (message: string) => {
                    // Show progress as toast notification
                    runInAction(() => {
                        this.showToastNotification('info', 'Roster Copy Progress', message);
                    });
                },
                (status: 'COMPLETED' | 'FAILED') => {
                    if (status === 'COMPLETED') {
                        runInAction(() => {
                            if (metadata.rosterId && metadata.revisionName) {
                                // Prevent duplicate: only add if not already present
                                const existingRoster = this._providerRosters.find(r => r.rosterId === metadata.rosterId.toString() && r.revisionName === metadata.revisionName);
                                let newRevisionId = null;
                                if (!existingRoster) {
                                    newRevisionId = this.addNewRevisionToRoster(metadata.rosterId, metadata.revisionName, metadata.notes || '');
                                } else {
                                    newRevisionId = existingRoster.revisionId;
                                }
                                // If there were provider changes, trigger updateRoster
                                if (hasProviderChanges && newRevisionId) {
                                    this.hideToastNotification();
                                    // Clear roster action loading since copy is complete
                                    this.isRosterActionLoading = false;
                                    this.handlePostCopyUpdate(newRevisionId, metadata);
                                } else {
                                    this.hideToastNotification();
                                    this.showToastNotification('success', 'Roster copied successfully', `${metadata.revisionName || 'Roster'} has been created`);
                                    this.isRosterActionLoading = false;
                                }
                            }
                        });
                    } else {
                        runInAction(() => {
                            this.hideToastNotification();
                            this.showToastNotification('error', 'Roster Copy Failed', 'Please try again or contact support.');
                            this.isRosterActionLoading = false;
                        });
                    }
                    // Clean up
                    streamPersistence.removeStream(streamId);
                    this._reconnectedStreams.delete(streamId);
                },
                (error) => {
                    console.error('SSE connection error:', error);
                    runInAction(() => {
                        this.isRosterActionLoading = false;
                        this.hideToastNotification();
                        this.showToastNotification('error', 'Connection Error', 'Lost connection to roster copy. Please try again.');
                    });
                    // Clean up on error
                    streamPersistence.removeStream(streamId);
                    this._reconnectedStreams.delete(streamId);
                }
            );
        } catch (error) {
            // Stream not found or error checking status
            console.error('Error checking stream status:', error);
            runInAction(() => {
                this.isRosterActionLoading = false;
                this.showToastNotification('error', 'Reconnection Failed', 'Could not reconnect to roster copy. Please try again.');
            });
            // Clean up the stale stream reference
            streamPersistence.removeStream(streamId);
            this._reconnectedStreams.delete(streamId);
        }
    }

    // Handle post-copy operations when provider changes exist
    private async handlePostCopyUpdate(newRevisionId: number, metadata: any) {
        try {
            // Use the saved context from metadata to call updateRoster
            // Don't modify the store - use values directly from metadata
            const controller = new AbortController();
            await this.updateRosterFromMetadata(
                metadata.runId,
                newRevisionId,
                metadata.specialtyFilterCode,
                metadata.selectedAdditionalProviderKeys,
                metadata.deselectedExistingProviderKeys,
                metadata.county,
                metadata.standardType,
                metadata.standardVersion,
                metadata.patientRosterId,
                controller
            );
        } catch (error) {
            console.error('Error during post-copy update:', error);
            runInAction(() => {
                this.showToastNotification('error', 'Failed to apply changes', 'Roster was copied but changes could not be applied.');
                this.isRosterActionLoading = false;
            });
        }
    }

    // Similar to updateRoster but uses explicit parameters instead of reading from store
    private async updateRosterFromMetadata(
        runId: number,
        revisionId: number,
        specialtyFilterCode: string,
        selectedAdditionalProviderKeys: string[],
        deselectedExistingProviderKeys: string[],
        county: string,
        standardType: string,
        standardVersion: string,
        patientRosterId: string,
        controller: AbortController
    ) {
        if (!runId || !specialtyFilterCode || (selectedAdditionalProviderKeys.length === 0 && deselectedExistingProviderKeys.length === 0))
            return;

        // Prevent multiple simultaneous recalculations
        if (this.loadingRevisionRecalculation) {
            this.showToastNotification('warning', 'Recalculation in Progress', 'Please wait for the current recalculation to complete.');
            return;
        }

        try {
            this.loadingRevisionRecalculation = true;
            this.showToastNotification('info', 'Recalculation Started', 'Analyzing network adequacy...');

            await networkOptimization.updateProviderRoster(runId, revisionId, specialtyFilterCode, selectedAdditionalProviderKeys, deselectedExistingProviderKeys, controller.signal);

            // Start SSE-based recalculation
            const {stream_id} = await networkOptimization.recalculateRevisionSSE(
                runId,
                revisionId,
                specialtyFilterCode,
                county,
                standardType,
                standardVersion,
                patientRosterId,
                controller.signal
            );

            // Save stream to sessionStorage for reconnection after page refresh
            streamPersistence.saveStream(stream_id, {
                streamType: 'recalculation',
                runId: runId,
                revisionId: revisionId,
                specialtyCode: specialtyFilterCode,
                timestamp: Date.now()
            });

            // Connect to SSE stream for progress updates
            await new Promise<void>(async (resolve, reject) => {
                const es = await createSSEConnection(
                    stream_id,
                    (message: string) => {
                        runInAction(() => {
                            this.showToastNotification('info', 'Recalculation Progress', message);
                        });
                    },
                    (status: 'COMPLETED' | 'FAILED') => {
                        if (status === 'COMPLETED') {
                            runInAction(() => {
                                this.rosterRunCount += 1;
                                this.hideToastNotification();
                                this.showToastNotification('success', 'Recalculation Complete', 'Network adequacy has been successfully updated.');
                            });
                            streamPersistence.removeStream(stream_id);
                            resolve();
                        } else {
                            runInAction(() => {
                                this.hideToastNotification();
                                this.showToastNotification('error', 'Recalculation Failed', 'Please try again or contact support.');
                            });
                            streamPersistence.removeStream(stream_id);
                            reject(new Error('Recalculation failed'));
                        }
                    },
                    (error) => {
                        console.error('SSE connection error:', error);
                    }
                );

                controller.signal.addEventListener('abort', () => {
                    es.close();
                    streamPersistence.removeStream(stream_id);
                    reject(new Error('Aborted'));
                });
            });
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            this.hideToastNotification();
            this.showToastNotification('error', 'Error', this.extractErrorMessage(error));
        }
        finally{
            this.loadingRevisionRecalculation = false;
        }
    }

    reset() {
        this.patientGeoJson = null;
        this._providerGeoJson = null;
        this._reportProviderGeoJson = null;
        this._reportProviderGeoJsonError = null;
        this._reportProviderGeoJsonLoading = false;
        this.appliedGeographyFilter = {
            state: '',
            county: '',
            patient_roster_name: '',
            standard_type: '',
            standard_version: ''
        };
        this._distanceCalcType = DISTANCE_CALC_TYPES[0].value;
        this.availableGeographyFilters = [];
        this.availableDisruptionAnalysisFilters = [];
        this.specialtyFilter = '';
        this.disruptionAnalysisSpecialtyFilter = '';
        this.specialties = [];
        this.rosterComparisonDisruptionAnalysisData = [];
        this.loadingRevisionRecalculation = false;
        this.isRosterActionLoading = false;
        this.loadingDisruptionAnalysisComparisonData = false;
        this.loadingNetworkAdequacyComparisonData = false;
        this.loadingGeographyData = false;
        this.loadingStandardVersions = false;
        this.loadingSpecialtyData = false;
        this.loadingDisruptionAnalysisSpecialtyData = false;
        this.defaultComplianceScores = [];
        this.complianceScores = [];
        this.disruptionAnalysisComplianceScores = [];
        this._localProviders = [];
        this._existingProviders = [];
        this._providerRosters = [];
        this._loadingProviderRosters = false;
        this.runId = null;
        this.error = null;
        this.navItems = [];
        this.revisionId = 0;
        this.rosterFileName = '';
        this.selectedAdditionalProviderKeys = [];
        this.deselectedExistingProviderKeys = [];
        this.existingProviderQueuedForDeletion = null;
        this.showRevertModal = false;
        this._reconnectedStreams.clear();
    }

    async loadGeographyData() {
        try {
            this.loadingGeographyData = true;
            const response = await networkOptimization.fetchGeographyFilters(this.runId! as number);
            runInAction(() => {
                this.availableGeographyFilters = response as Array<GeographyFilterOptions>;
                this.resetGeographyFilter();
            });
        } catch (error) {
            this.error = this.extractErrorMessage(error);
        } finally {
            this.loadingGeographyData = false;
        }
    }

    async loadDisruptiveAnalysisFilterData() {
        try {
            this.loadingDisruptionAnalysisData = true;
            const response = await networkOptimization.fetchDisruptiveAnalysisFilters(this.runId! as number);
            runInAction(() => {
                this.availableDisruptionAnalysisFilters = response as Array<DisruptionAnalysisFilterOptions>;
                this.resetDisruptionAnalysisFilter();
            });
        } catch (error) {
            this.error = this.extractErrorMessage(error);
        } finally {
            this.loadingDisruptionAnalysisData = false;
        }
    }

    getGeoFilterUniqueValuesByKey(
        filterType: 'GEOGRAPHY' | 'DISRUPTION_ANALYSIS',
        key: string | string[],
        filter?: Record<string, string>,
        separator?: string,
        sortOrder?: 'ASC' | 'DESC'
    ): string[] {
        let selectedFilters: (GeographyFilterOptions | DisruptionAnalysisFilterOptions)[] = filterType === 'GEOGRAPHY' ? this.availableGeographyFilters as GeographyFilterOptions[] : this.availableDisruptionAnalysisFilters as DisruptionAnalysisFilterOptions[];

        if (separator === undefined || separator === null) {
            separator = '-';
        }
        
        if (sortOrder === undefined || sortOrder === null) {
            sortOrder = 'ASC';
        }

        // Helper to get nested value by path (array of keys)
        const getValueByPath = (obj: any, path: string | string[]) => {
            const keys = Array.isArray(path) ? path : path.split('.');
            return keys.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
        };

        // Apply filter if provided
        if (filter && Object.keys(filter).length > 0) {
            selectedFilters = selectedFilters.filter(item =>
                Object.entries(filter).every(([fKey, fVal]) => {
                    // Skip empty filter values
                    if (!fVal || fVal === '') return true;

                    const val = getValueByPath(item, fKey);

                    // Handle pipe-separated values (multiple selections)
                    if (fVal.includes('|')) {
                        const filterValues = fVal.split('|').filter(v => v.trim() !== '');
                        // Return true if the item value matches ANY of the filter values
                        return filterValues.includes(val);
                    }

                    // Single value comparison
                    return val === fVal;
                })
            );
        }

        const values = selectedFilters
            .map(item => {
                if (Array.isArray(key)) {
                    const parts = key.map(k => getValueByPath(item, k));
                    return parts.every(p => typeof p === 'string') ? parts.join(separator) : undefined;
                } else {
                    return getValueByPath(item, key);
                }
            })
            .filter(val => typeof val === 'string');

        // Unique values
        const uniqueValues = Array.from(new Set(values));

        // Sort values
        uniqueValues.sort((a, b) => sortOrder === 'ASC' ? a.localeCompare(b) : b.localeCompare(a));

        return uniqueValues;
    }

    //fetchNavheaders
    async loadHeaders() {
        try {
            const response = await networkOptimization.fetchNavItems();
            runInAction(() => {
                this.navItems = response;
            });
        } catch (error) {
            console.error('Failed to fetch navigation items:', error);
        }
    }

    // Fetch and update the map data
    async loadMapGeoJson(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        try {
            let filters: Record<string, string> = {
                ...this.geographyFilters
            };
            if (this.specialtyFilter)
                filters.specialty = this.specialtyFilterCode;
            const patientServiceCall: any = this.specialtyFilter ? networkOptimization.fetchPatientGeoJson : networkOptimization.fetchPatientPopulationGeoJson;

            this.patientGeoJson = null;
            this._providerGeoJson = null;

            const [patientData, providerData] = await Promise.all([
                patientServiceCall(filters, this.runId!, this.revisionId, this._distanceCalcType, controller.signal),
                networkOptimization.fetchProviderGeoJson(filters, this.runId!, this.revisionId, controller.signal)
            ]) as [FetchGeoJsonResponse, FetchGeoJsonResponse];
            this.patientGeoJson = patientData;
            this._providerGeoJson = providerData;
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            console.error('Failed to fetch map data:', error);
        }
    }


    // Fetch and update the disruption analysis map data
    async disruptionAnalysisLoadMapGeoJson(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        try {
            let filters: Record<string, string> = {
                ...this.disruptionAnalysisFilters
            };
            if (this.disruptionAnalysisSpecialtyFilter)
                filters.specialty = this.disruptionAnalysisSpecialtyFilterCode;
            const patientServiceCall: any = this.disruptionAnalysisSpecialtyFilter ? networkOptimization.fetchDAPatientGeoJson : networkOptimization.fetchDAPatientPopulationGeoJson;
            this.patientGeoJson = null;
            this._providerGeoJson = null;
            const [patientData, providerData] = await Promise.all([
                patientServiceCall(filters, this.runId!, this.revisionId, controller.signal),
                networkOptimization.fetchDAProviderGeoJson(filters, this.runId!, this.revisionId, controller.signal)
            ]) as [FetchGeoJsonResponse, FetchGeoJsonResponse];
            this.patientGeoJson = patientData;
            this._providerGeoJson = providerData;
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            console.error('Failed to fetch map data:', error);
        }
    }

    // Fetch and update the local providers list
    async loadLocalProviders(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        try {
            let filters: Record<string, string> = {
                ...this.geographyFilters
            };
            if (this.specialtyFilter)
                filters.specialty = this.specialtyFilterCode;
            let [localProvidersResponse, existingProviders]  = await Promise.all([networkOptimization.fetchProviders(filters, this.runId!, this.revisionId, this._distanceCalcType, controller.signal),
                networkOptimization.fetchProviderReport(filters, this.runId!, this.revisionId, controller.signal)]) as [any, FetchProvidersResponse];

            // Handle paginated response format
            let localProviders: FetchProvidersResponse;
            let paginationInfo: {current_page: number, total_pages: number, total_count: number} | null = null;

            if (Array.isArray(localProvidersResponse)) {
                // Backward compatible: array response when count <= 5000
                localProviders = localProvidersResponse;
            } else {
                // Paginated response when count > 5000
                localProviders = localProvidersResponse.data || [];
                paginationInfo = {
                    current_page: localProvidersResponse.current_page,
                    total_pages: localProvidersResponse.total_pages,
                    total_count: localProvidersResponse.total_count
                };
            }

            this._localProviders = localProviders.map(provider => ({...provider, id: provider.provider_address_fact_key}));
            this._existingProviders = existingProviders.map(provider => ({...provider, id: provider.provider_address_fact_key}));

            return paginationInfo;
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            throw error;
        }
    }

    // Fetch and update the local disruption analysis providers list
    async loadDisruptionAnalysisLocalProviders(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        try {
            let filters: Record<string, string> = {
                ...this.disruptionAnalysisFilters
            };
            if (this.disruptionAnalysisSpecialtyFilter)
                filters.specialty = this.disruptionAnalysisSpecialtyFilterCode;
            let [localProviders, existingProviders]  = await Promise.all([networkOptimization.fetchDAProviders(filters, this.runId!, this.revisionId, controller.signal),
                networkOptimization.fetchDAProviderReport(filters, this.runId!, this.revisionId, controller.signal)]) as [FetchProvidersResponse, FetchProvidersResponse];
            this._localProviders = localProviders.map(provider => ({...provider, id: provider.provider_address_key}));
            this._existingProviders = existingProviders.map(provider => ({...provider, id: provider.provider_address_key}));
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            throw error;
        }
    }

    async loadDisruptionAnalysisComparison(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        try {
            this.loadingDisruptionAnalysisComparisonData = true;
            let filters: Record<string, string| Array<string>> = {
                ...this.disruptionAnalysisFilters,
                selectedRosters: this.selectedRosters
            };
            delete filters.county; // removes 'county' property
            delete filters.zip; // removes 'zip' property
            delete filters.patient_roster_id; // removes 'patient_roster_id' property
            delete filters.state; // removes 'state' property
            filters.specialty = this.disruptionAnalysisSpecialtyFilterCode;
            const response = await networkOptimization.fetchDisruptionAnalysisComparison(filters, this.runId!, controller.signal);
            runInAction(() => {
                this.rosterComparisonDisruptionAnalysisData = response;
            });
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            throw error;
        }
        finally {
            this.loadingDisruptionAnalysisComparisonData = false;
        }
    }

    async loadNetworkAdequacyComparison(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        try {
            this.loadingNetworkAdequacyComparisonData = true;
            let filters: Record<string, string| Array<string>> = {
                ...this.geographyFilters,
                selectedRosters: this.selectedRosters
            };
            filters.specialty = this.specialtyFilterCode;
            const response = await networkOptimization.fetchNetworkAdequacyComparison(filters, this.runId!, controller.signal);
            runInAction(() => {
                this.rosterComparisonNetworkAdequacyData = response;
            });
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            throw error;
        }
        finally {
            this.loadingNetworkAdequacyComparisonData = false;
        }
    }

    async loadSpecialtiesData(controller: AbortController, provider_facility_type = '', includeStateAndCounty = true) {
        if (!this.readyToLoadData())
            return;
        try {
            this.loadingSpecialtyData = true;
            let filters = {
                ...this.geographyFilters,
                provider_facility_type
            };

            if (provider_facility_type || !includeStateAndCounty)
            {
                filters = {...filters, state: '', county: ''};
            }

            const adequateUnitLables: { [key: string]: string } = {
                npi: '# of Providers',
                npi_lat_long: '# of Provider Locations',
                beds: '# of Beds'
            };
            const isStateAndCountySelected = this.appliedGeographyFilter.state && this.appliedGeographyFilter.county && includeStateAndCounty;
            const result = await networkOptimization.fetchSpecialtyReport(filters, this.runId!, this.revisionId, this._distanceCalcType, controller.signal);
            const specialtyReport = result.adequacy_data;

            runInAction(() => {
                if (isStateAndCountySelected) {
                    this.defaultComplianceScores = [];
                    this.specialties = Array.from(new Set(specialtyReport.map((item: any) => item.specialty_code)));
                    this.complianceScores = specialtyReport.map((item: any) => ({
                        name: item.specialty_name,
                        specialtyCode: item.specialty_code,
                        compliance: item.final_adequacy_flag === '1',
                        revision_id: item.revision_id === '1',
                        compliance_target: (item.compliance_target * 100).toFixed(1),
                        scores: [
                            {name: 'Time', value: this.formatPercentage(item.population_met_time), compliant: item.time_adequacy_flag === '1'},
                            {name: 'Distance', value: this.formatPercentage(item.population_met_distance), compliant: item.distance_adequacy_flag === '1'},
                            {name: adequateUnitLables[item.minimum_required_method] || '# of Provider Locations', value: item.total_adequate_units, compliant: item.min_docs_flag === '1'},
                            {name: 'Estimated Total Compliance', value: null, compliant: item.final_adequacy_flag === '1', cssClasses: item.final_adequacy_flag === '1' ? 'bg-green-300 font-bold' : 'bg-red-300 font-bold'},
                            {name: 'Min Required', value: item.minimum_required, compliant: item.min_required_adequacy_flag < item.total_adequate_units}
                        ]
                    }));
                } else {
                    this.complianceScores = specialtyReport;
                    this.defaultComplianceScores = result.default_specialty_filter_data.map((item: any) => ({
                        name: item.specialty_name,
                        specialtyCode: item.specialty_code}));
                }
                this.loadingSpecialtyData = false;
            });
        } catch (error) {
            this.specialties = [];
            this.complianceScores = [];
            this.defaultComplianceScores = [];
            this.error = this.extractErrorMessage(error);
            console.error('Failed to fetch specialties and compliance scores:', error);
        }
    }

    async loadDisruptionAnalysisSpecialtiesData(controller: AbortController) {
        if (!this.readyToLoadData())
            return;
        try {
            this.loadingDisruptionAnalysisSpecialtyData = true;
            const filters = {
                ...this.disruptionAnalysisFilters,
                specialty: this.disruptionAnalysisSpecialtyFilterCode
            };
            const result = await networkOptimization.fetchDisruptionAnalysisSpecialtyReport(filters, this.runId!, this.revisionId, controller.signal);
            runInAction(() => {
                this.disruptionAnalysisComplianceScores = result;
                this.loadingDisruptionAnalysisSpecialtyData = false;
            });
        } catch (error) {
            this.specialties = [];
            this.disruptionAnalysisComplianceScores = [];
            this.error = this.extractErrorMessage(error);
            this.loadingDisruptionAnalysisSpecialtyData = false;
            console.error('Failed to fetch specialties and compliance scores:', error);
        }
    }

    async loadProviderRosters(controller: AbortController) {
        try {
            this._loadingProviderRosters = true;
            const data = await networkOptimization.listProviderRosters(controller.signal);
            runInAction(() => {
                this._providerRosters = data;
                this._loadingProviderRosters = false;
            });
        } catch (error) {
            runInAction(() => {
                console.error('Error loading provider rosters:', error);
                this._loadingProviderRosters = false;
            });
        }
    }

    async loadPatientRosters(controller: AbortController) {
        try {
            this._loadingPatientRosters = true;
            const data = await networkOptimization.listPatientRosters(controller.signal);
            runInAction(() => {
                this._patientRosters = data;
                this._loadingPatientRosters = false;
            });
        } catch (error) {
            runInAction(() => {
                console.error('Error loading patient rosters:', error);
                this._loadingPatientRosters = false;
            });
        }
    }

    async updateRoster(controller: AbortController) {
        if (!this.runId || !this.specialtyFilterCode || (this.selectedAdditionalProviderKeys.length === 0 && this.deselectedExistingProviderKeys.length === 0))
            return;

        // Call the internal method with values from the store
        await this.updateRosterFromMetadata(
            this.runId,
            this.revisionId,
            this.specialtyFilterCode,
            this.selectedAdditionalProviderKeys,
            this.deselectedExistingProviderKeys,
            this.geographyFilters.county,
            this.appliedGeographyFilter.standard_type,
            this.appliedGeographyFilter.standard_version,
            this.geographyFilters.patient_roster_id,
            controller
        );
    }

    toggleRevision() {
        this.revisionId = this.revisionId ? 0 : 1;
    }

    calculateGeneralComplianceScores() {
        if (this.readyToLoadData() && this.complianceScores.length > 0 && (this.providerGeoJson && this.providerGeoJson?.features?.length > 0))
            return {
                scores: [
                    {name: '# of Provider Locations', value: this.providerGeoJson.features.length, compliant: null},
                    {name: 'Estimated Total Compliance', value: `${this.complianceScores.filter(cs => cs.compliance).length} / ${this.complianceScores.length}`, compliant: null}
                ]
            };
        return {scores: []};
    }

    // Setters for applied filters
    setGeographyFilter(filterName: 'state' | 'county' | 'standard_type' | 'patient_roster_name' | 'standard_version', value: string) {
        if (this.appliedGeographyFilter[filterName] !== value) {
            this.appliedGeographyFilter = {...this.appliedGeographyFilter, [filterName]: value};
            switch (filterName) {
            case 'standard_type': {
                const defaultStandardVersion = this.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'standard_version', {'standard_type': this.appliedGeographyFilter.standard_type}, '', 'DESC')[0];
                const defaultPatientRosterName = this.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'patient_roster_name', {'standard_type': this.appliedGeographyFilter.standard_type, 'standard_version': defaultStandardVersion})[0] || '';
                this.appliedGeographyFilter = {...this.appliedGeographyFilter, standard_version: defaultStandardVersion, patient_roster_name: defaultPatientRosterName, state: '', county: ''};
                break;
            }
            case 'standard_version': {
                const defaultPatientRoster = this.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'patient_roster_name', {'standard_type': this.appliedGeographyFilter.standard_type, 'standard_version': this.appliedGeographyFilter.standard_version})[0] || '';
                this.appliedGeographyFilter = {...this.appliedGeographyFilter, patient_roster_name: defaultPatientRoster, state: '', county: ''};
                break;
            }
            case 'patient_roster_name': {
                this.appliedGeographyFilter = {...this.appliedGeographyFilter, state: '', county: ''};
                break;
            }
            case 'state': {
                this.appliedGeographyFilter = {...this.appliedGeographyFilter, county: ''};
                break;
            }}
            this.specialtyFilter = '';

        }
    }

    setDisruptionAnalysisFilter(filterName: 'state' | 'county' | 'standard_type' | 'patient_roster_name' | 'zip', value: string) {
        if (this.appliedDisruptionAnalysisFilter[filterName] !== value) {
            switch (filterName) {
            case 'standard_type': {
                const defaultPatientRosterName = this.getGeoFilterUniqueValuesByKey(
                    'DISRUPTION_ANALYSIS',
                    'patient_roster_name',
                    {standard_type: value}
                )[0] || '';
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    standard_type: value,
                    patient_roster_name: defaultPatientRosterName,
                    state: '',
                    county: '',
                    zip: ''
                };
                this.disruptionAnalysisSpecialtyFilter = '';
                break;
            }
            case 'patient_roster_name': {
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    patient_roster_name: value,
                    state: '',
                    county: '',
                    zip: ''
                };
                this.disruptionAnalysisSpecialtyFilter = '';
                break;
            }
            case 'state': {
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    state: value,
                    county: '',
                    zip: ''
                };
                this.disruptionAnalysisSpecialtyFilter = '';
                break;
            }
            case 'county': {
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    county: value,
                    zip: ''
                };
                break;
            }
            case 'zip': {
                this.appliedDisruptionAnalysisFilter = {
                    ...this.appliedDisruptionAnalysisFilter,
                    zip: value
                };
                break;
            }}
        }
    }

    resetGeographyFilter(preserve: Array<keyof AppliedGeographyFilter> = []) {
        const current = this.appliedGeographyFilter;
        const standardType = preserve.includes('standard_type')
            ? current.standard_type
            : (this.availableGeographyFilters[0]?.standard_type || '');

        const standardVersion = preserve.includes('standard_version')
            ? current.standard_version
            : (this.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'standard_version', {standard_type: standardType}, '', 'DESC')[0] || '');

        const patientRosterName = preserve.includes('patient_roster_name')
            ? current.patient_roster_name
            : (this.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'patient_roster_name', {standard_type: standardType, standard_version: standardVersion})[0] || '');

        const state = preserve.includes('state') ? current.state : '';
        const county = preserve.includes('county') ? current.county : '';

        this.appliedGeographyFilter = {
            state,
            county,
            patient_roster_name: patientRosterName,
            standard_type: standardType,
            standard_version: standardVersion
        };
        this.specialtyFilter = '';
    }

    resetDisruptionAnalysisFilter() {
        const defaultLineOfBusiness = this.availableDisruptionAnalysisFilters[0]?.standard_type || '';
        const defaultPatientRoster = this.getGeoFilterUniqueValuesByKey(
            'DISRUPTION_ANALYSIS',
            'patient_roster_name',
            {standard_type: defaultLineOfBusiness}
        )[0] || '';

        this.appliedDisruptionAnalysisFilter = {
            state: '',
            county: '',
            patient_roster_name: defaultPatientRoster,
            standard_type: defaultLineOfBusiness,
            zip: ''
        };
        this.disruptionAnalysisSpecialtyFilter = '';
    }

    setRevisionId(value: number) {
        this.revisionId = value;
    }

    setSpecialtyFilter(value: string) {
        this.specialtyFilter = value;
    }

    setDisruptionAnalysisSpecialtyFilter(value: string) {
        this.disruptionAnalysisSpecialtyFilter = value;
    } 

    setAdditionalProviderVisibleColumns(columns: string[]) {
        this.additionalProviderVisibleColumns = columns;
    }

    setAdditionalProviderPendingColumns(columns: string[]) {
        this.additionalProviderPendingColumns = columns;
    }

    // Toast notification methods
    showToastNotification(kind: 'success' | 'error' | 'info' | 'warning', title: string, subtitle = '') {
        this.toastKind = kind;
        this.toastTitle = title;
        this.toastSubtitle = subtitle;
        this.showToast = true;
    }

    hideToastNotification() {
        this.showToast = false;
    }

    async revertProviderRosterToOriginal(controller: AbortController) {
        if (!this.runId || !this.specialtyFilterCode)
            return;
        await networkOptimization.resyncProviderRosterRevision(this.runId, this.revisionId, this.specialtyFilterCode, controller.signal);
        this.revisionId = 0;
    }

    async copyRoster(rosterId: number, revisionName: string, notes: string, controller: AbortController) {
        // Prevent multiple simultaneous roster copies
        if (this.isRosterActionLoading) {
            this.showToastNotification('warning', 'Roster Copy in Progress', 'Please wait for the current roster copy to complete.');
            return {success: false, message: 'Roster copy already in progress', revisionId: null};
        }

        try {
            this.isRosterActionLoading = true;
            this.showToastNotification('info', 'Roster Copy Started', 'Copying roster...');

            // Start SSE-based roster copy
            const {stream_id} = await networkOptimization.copyRosterSSE(rosterId, revisionName, notes, controller.signal);

            // Save stream to sessionStorage for reconnection after page refresh
            // Include only the values needed for updateRoster if provider changes exist
            streamPersistence.saveStream(stream_id, {
                streamType: 'roster_copy',
                rosterId,
                revisionName,
                notes,
                // Save provider changes - if these exist, we need to call updateRoster after copy completes
                selectedAdditionalProviderKeys: this.selectedAdditionalProviderKeys,
                deselectedExistingProviderKeys: this.deselectedExistingProviderKeys,
                // Save context needed for updateRoster call (if provider changes exist)
                runId: this.runId,
                specialtyFilterCode: this.specialtyFilterCode,
                county: this.geographyFilters.county,
                standardType: this.appliedGeographyFilter.standard_type,
                standardVersion: this.appliedGeographyFilter.standard_version,
                patientRosterId: this.geographyFilters.patient_roster_id,
                timestamp: Date.now()
            });

            let newRevisionId: number | null = null;

            // Connect to SSE stream for progress updates
            await new Promise<void>(async (resolve, reject) => {
                const es = await createSSEConnection(
                    stream_id,
                    (message: string) => {
                        // Show progress as toast notification
                        runInAction(() => {
                            this.showToastNotification('info', 'Roster Copy Progress', message);
                        });
                    },
                    (status: 'COMPLETED' | 'FAILED') => {
                        if (status === 'COMPLETED') {
                            runInAction(() => {
                                newRevisionId = this.addNewRevisionToRoster(rosterId, revisionName, notes);
                                this.hideToastNotification();
                                this.showToastNotification('success', 'Roster copied successfully', `${revisionName} has been created`);
                            });
                            // Remove from storage when complete
                            streamPersistence.removeStream(stream_id);
                            resolve();
                        } else {
                            runInAction(() => {
                                this.hideToastNotification();
                                this.showToastNotification('error', 'Failed to copy roster', 'Please try again');
                            });
                            // Remove from storage on failure
                            streamPersistence.removeStream(stream_id);
                            reject(new Error('Roster copy failed'));
                        }
                    },
                    (error) => {
                        console.error('SSE connection error:', error);
                    }
                );

                // Clean up on abort
                controller.signal.addEventListener('abort', () => {
                    es.close();
                    streamPersistence.removeStream(stream_id);
                    reject(new Error('Aborted'));
                });
            });

            return {success: true, message: 'Roster copied successfully', revisionId: newRevisionId};
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            this.hideToastNotification();
            this.showToastNotification('error', 'Error copying roster', this.extractErrorMessage(error));
            return {success: false, message: this.error, revisionId: null};
        } finally {
            this.isRosterActionLoading = false;
        }
    }

    async renameRoster(rosterId: number, revisionId: number, revisionName: string, notes: string | null, controller: AbortController) {
        try {
            this.isRosterActionLoading = true;
            await networkOptimization.renameRoster(rosterId, revisionId, revisionName, notes, controller.signal);
            this.updateRevisionInRoster(rosterId, revisionId, revisionName, notes);
            this.showToastNotification('success', 'Roster renamed successfully', `Updated to ${revisionName}`);
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            this.showToastNotification('error', 'Failed to rename roster', 'Something went wrong. Please try again.');
            throw error;
        } finally {
            this.isRosterActionLoading = false;
        }
    }

    async deleteRoster(rosterId: number, revisionId: number, controller: AbortController) {
        try {
            this.isRosterActionLoading = true;
            await networkOptimization.deleteRoster(rosterId, revisionId, controller.signal);
            this.deleteRevisionFromRoster(rosterId, revisionId);
            this.showToastNotification('success', 'Roster deleted successfully', 'The roster revision has been removed');
        } catch (error) {
            this.error = this.extractErrorMessage(error);
            this.showToastNotification('error', 'Failed to delete roster', 'Something went wrong. Please try again.');
            throw error;
        } finally {
            this.isRosterActionLoading = false;
        }
    }
   
    private addNewRevisionToRoster(rosterId: number, revisionName: string, notes: string | null): number {
        let newRevisionIdNum = 0;
        runInAction(() => {
            // Find all rosters with the same rosterId to determine the next revision ID
            const rostersWithSameId = this._providerRosters.filter(r => r.rosterId === rosterId.toString());
            if (rostersWithSameId.length > 0) {
                // Get the base roster info from any existing roster
                const baseRoster = rostersWithSameId[0];
                
                // Calculate the next revision ID by finding the max revision ID and incrementing
                const maxRevisionId = Math.max(
                    ...rostersWithSameId.map((r: any) => parseInt(r.revisionId, 10))
                );
                const nextRevisionId = (maxRevisionId + 1).toString();
                newRevisionIdNum = maxRevisionId + 1;
                
                // Get logged-in user email from auth
                const currentUser = auth.principal?.email || '';
                const currentDate = new Date().toISOString();
                
                // Create a new revision
                const newRevision = {
                    createdAt: baseRoster.createdAt,
                    createdBy: baseRoster.createdBy,
                    dateModified: baseRoster.dateModified,
                    modifiedBy: baseRoster.modifiedBy,
                    revisionCreatedBy: currentUser,
                    revisionDateCreated: currentDate,
                    revisionDateModified: currentDate,
                    revisionId: nextRevisionId,
                    revisionModifiedBy: currentUser,
                    revisionName: revisionName,
                    revisionNotes: notes,
                    rosterId: rosterId.toString(),
                    rosterName: baseRoster.rosterName
                };
                
                this._providerRosters = [...this._providerRosters, newRevision];
            }
        });
        return newRevisionIdNum;
    }

    private updateRevisionInRoster(rosterId: number, revisionId: number, revisionName: string, notes: string | null) {
        runInAction(() => {
            const rosterIndex = this._providerRosters.findIndex(
                r => r.rosterId === rosterId.toString() && r.revisionId === revisionId.toString()
            );
            
            if (rosterIndex !== -1) { 
                // Update in place 
                this._providerRosters[rosterIndex] = {
                    ...this._providerRosters[rosterIndex],
                    revisionName: revisionName,
                    revisionNotes: notes,     
                };
                this._providerRosters = [...this._providerRosters];
            }
        });
    }

    private deleteRevisionFromRoster(rosterId: number, revisionId: number) {
        runInAction(() => {
            // Remove the roster with the matching rosterId and revisionId
            this._providerRosters = [...this._providerRosters.filter(
                r => !(r.rosterId === rosterId.toString() && r.revisionId === revisionId.toString())
            )];
        });
    }

    get geographyFilters(): IGeographyFilterPayload | Record<string, never> {
        // Helper function to process pipe-separated values
        const processPipeSeparatedValue = (
            value: string, 
            filterArray: GeographyFilterOptions[], 
            findFn: (item: GeographyFilterOptions, val: string) => boolean,
            extractFn: (item: GeographyFilterOptions) => string | undefined
        ): string => {
            if (!value) return '';
            
            if (value.includes('|')) {
                const values = value.split('|').filter(v => v.trim() !== '');
                const results = values
                    .map(v => {
                        const found = filterArray.find(item => findFn(item, v));
                        return found ? extractFn(found) : null;
                    })
                    .filter(r => r !== undefined && r !== null && r !== '');
                return results.join('|');
            }
            
            const found = filterArray.find(item => findFn(item, value));
            return found ? (extractFn(found) || '') : '';
        };

        const state = processPipeSeparatedValue(
            this.appliedGeographyFilter.state,
            this.availableGeographyFilters,
            (item, val) => item?.state_additional_info?.state === val,
            (item) => item?.state
        );

        const county = processPipeSeparatedValue(
            this.appliedGeographyFilter.county,
            this.availableGeographyFilters,
            (item, val) => `${item?.county_additional_info?.county_name}-${item.ssacd}` === val,
            (item) => item?.ssacd
        );

        const patient_roster_id = this.availableGeographyFilters.find(
            o => o?.patient_roster_name === this.appliedGeographyFilter.patient_roster_name
        )?.patient_roster_id;

        const standard_version = this.appliedGeographyFilter.standard_version;
        const standard_type = this.appliedGeographyFilter.standard_type;

        return {
            state: state || '',
            county: county || '',
            standard_version: standard_version || '',
            standard_type: standard_type || '',
            patient_roster_id: patient_roster_id || ''
        };
    }

    get disruptionAnalysisFilters(): IDisruptionAnalysisFilterPayload | Record<string, never> {
        // Helper function to process pipe-separated values
        const processPipeSeparatedValue = (
            value: string,
            filterArray: DisruptionAnalysisFilterOptions[],
            findFn: (item: DisruptionAnalysisFilterOptions, val: string) => boolean,
            extractFn: (item: DisruptionAnalysisFilterOptions) => string | undefined
        ): string => {
            if (!value) return '';
            
            if (value.includes('|')) {
                const values = value.split('|').filter(v => v.trim() !== '');
                const results = values
                    .map(v => {
                        const found = filterArray.find(item => findFn(item, v));
                        return found ? extractFn(found) : null;
                    })
                    .filter(r => r !== undefined && r !== null && r !== '');
                return results.join('|');
            }
            
            const found = filterArray.find(item => findFn(item, value));
            return found ? (extractFn(found) || '') : '';
        };

        const state = processPipeSeparatedValue(
            this.appliedDisruptionAnalysisFilter.state,
            this.availableDisruptionAnalysisFilters,
            (item, val) => item?.state_additional_info?.state === val,
            (item) => item?.state
        );

        const county = processPipeSeparatedValue(
            this.appliedDisruptionAnalysisFilter.county,
            this.availableDisruptionAnalysisFilters,
            (item, val) => `${item?.county_additional_info?.county_name}-${item.ssacd}` === val,
            (item) => item?.ssacd
        );

        const patient_roster_id = this.availableDisruptionAnalysisFilters.find(
            o => o?.patient_roster_name === this.appliedDisruptionAnalysisFilter.patient_roster_name
        )?.patient_roster_id;

        const standard_type = this.appliedDisruptionAnalysisFilter.standard_type;
        const zip = this.appliedDisruptionAnalysisFilter.zip;

        return {
            state: state || '',
            county: county || '',
            standard_type: standard_type || '',
            patient_roster_id: patient_roster_id || '',
            zip: zip || ''
        };
    }

    /**
     * Helper method to convert specialty filter name(s) to specialty code(s)
     * Handles both single specialty names and pipe-separated multiple specialties
     */
    private getSpecialtyCodeFromFilter(filterValue: string): string {
        if (!filterValue) return '';

        const scores = this.defaultComplianceScores.length > 0
            ? this.defaultComplianceScores
            : this.complianceScores;

        // Handle pipe-separated specialty names
        if (filterValue.includes('|')) {
            const names = filterValue.split('|').filter((n: string) => n.trim() !== '');
            const codes = names
                .map((name: string) => scores.find(o => o.name === name)?.specialtyCode)
                .filter((code: any) => code !== undefined && code !== null && code !== '');
            return codes.length > 0 ? codes.join('|') : '';
        }

        // Single specialty name
        return scores.find(o => o.name === filterValue)?.specialtyCode || '';
    }

    get specialtyFilterCode(): string {
        return this.getSpecialtyCodeFromFilter(this.specialtyFilter);
    }

    get disruptionAnalysisSpecialtyFilterCode(): string {
        return this.getSpecialtyCodeFromFilter(this.disruptionAnalysisSpecialtyFilter);
    }

    readyToLoadData() {
        const geographyReady = this.appliedGeographyFilter.standard_type && this.appliedGeographyFilter.standard_version;
        const disruptionReady = this.appliedDisruptionAnalysisFilter.standard_type && this.appliedDisruptionAnalysisFilter.patient_roster_name;
        return (geographyReady || disruptionReady) && this.runId;
    }

    private formatPercentage(value: number) {
        return `${(value * 100).toFixed(1)}%`;
    }

    // Extract error message from the error object
    private extractErrorMessage(error: any): string {
        if (error instanceof Error) {
            return error.message;
        }
        return 'An unexpected error occurred';
    }

    selectAdditionalProviders(keys: string[]) {
        const existingProviders = this._existingProviders.filter(p => keys.includes(p['id']));
        this.deselectedExistingProviderKeys = this.deselectedExistingProviderKeys.filter(n => !existingProviders.some(p => p['id'] === n));
        const newProviders = keys.filter(k => !this._existingProviders.some(p => p['id'] === k));
        this.selectedAdditionalProviderKeys = this.selectedAdditionalProviderKeys.concat(newProviders);
    }

    deselectAdditionalProviders(keys: string[]) {
        this.selectedAdditionalProviderKeys = this.selectedAdditionalProviderKeys.filter(n => !keys.includes(n));
    }

    deselectAllAdditionalProviders() {
        this.selectedAdditionalProviderKeys = [];
    }

    get selectedAdditionalProviders() {
        return this.localProviders.filter(p => this.selectedAdditionalProviderKeys.some(s => s === p['id']));
    }

    get nonSelectedAdditionalProviders() {
        return this.localProviders.filter(p => !this.selectedAdditionalProviderKeys.some(s => s === p['id']));
    }

    selectExistingProviders(keys: string[]) {
        this.deselectedExistingProviderKeys = this.deselectedExistingProviderKeys.filter(n => !keys.includes(n));
    }

    selectAllExistingProviders() {
        this.deselectedExistingProviderKeys = [];
    }

    deselectExistingProviders(keys: string[]) {
        this.deselectedExistingProviderKeys = this.deselectedExistingProviderKeys.concat(keys);
    }

    get deselectedExistingProviders() {
        return this._existingProviders.filter(p => this.deselectedExistingProviderKeys.some(s => s === p['id']));
    }

    get providerGeoJson(): FetchGeoJsonResponse | null {
        return this._providerGeoJson ? {...this._providerGeoJson, features: this._providerGeoJson.features.filter(f => !this.deselectedExistingProviderKeys.some(p => p === f.properties!.provider_address_fact_key))} : this._providerGeoJson;
    }

    set providerGeoJson(value: FetchGeoJsonResponse | null) {
        this._providerGeoJson = value;
    }

    get reportProviderGeoJson(): FetchGeoJsonResponse | null {
        return this._reportProviderGeoJson;
    }

    get reportProviderGeoJsonError(): string | null {
        return this._reportProviderGeoJsonError;
    }

    set reportProviderGeoJsonError(value: string | null) {
        this._reportProviderGeoJsonError = value;
    }

    get reportProviderGeoJsonLoading(): boolean {
        return this._reportProviderGeoJsonLoading;
    }

    set reportProviderGeoJsonLoading(value: boolean) {
        this._reportProviderGeoJsonLoading = value;
    }

    get localProviders(): FetchProvidersResponse {
        return this.filterProviderList(this._localProviders).concat(this.deselectedExistingProviders);
    }

    appendLocalProviders(providers: FetchProvidersResponse) {
        this._localProviders.push(...providers);
    }

    get existingProviders() {
        return this._existingProviders.filter(p => !this.deselectedExistingProviderKeys.some(s => s === p['id']));
    }

    get distanceCalcType() {
        return this._distanceCalcType;
    }

    set distanceCalcType(value: string) {
        this._distanceCalcType = value;
    }

    get selectedRosters() {
        return this._selectedRosters;
    }

    set selectedRosters(value: any[]) {
        this._selectedRosters = value;
    }

    get selectedRosterForComparison() {
        return this._selectedRosterForComparison;
    }

    set selectedRosterForComparison(value: string) {
        this._selectedRosterForComparison = value;
    }

    get providerRosters() {
        return this._providerRosters;
    }

    set providerRosters(value: any[]) {
        this._providerRosters = value;
    }

    get loadingProviderRosters() {
        return this._loadingProviderRosters;
    }

    get patientRosters() {
        return this._patientRosters;
    }

    set patientRosters(value: any[]) {
        this._patientRosters = value;
    }

    get loadingPatientRosters() {
        return this._loadingPatientRosters;
    }

    filterProviderList(providerList: any[]) {
        let filteredList = providerList;

        if (this) {
            this.filters.forEach(filter => {
                filteredList = applyFilter(filteredList, filter);
            });
        }

        return filteredList;
    }

    async downloadReport(payload: IFetchReportsPayload, reportType: 'providers' | 'available-providers' | 'adequacy-scores' | 'standard-reporting', itemName: string) {
        this.downloadingItems[itemName] = true;
        try {
            let response;
            let filename;

            switch (reportType) {
            case 'providers':
                response = await networkOptimization.download_providers_report(payload);
                filename = 'providers-report.zip';
                break;
            case 'available-providers':
                response = await networkOptimization.download_available_providers_report(payload);
                filename = 'available-providers-report.zip';
                break;
            case 'adequacy-scores':
                response = await networkOptimization.download_adequacy_scores_report(payload);
                filename = 'adequacy-scores-report.zip';
                break;
            case 'standard-reporting':
                response = await networkOptimization.download_standard_report(payload);
                filename = 'standard-reporting.zip';
                break;
            default:
                throw new Error(`Unknown report type: ${reportType}`);
            }
            
            download(response, filename, 'application/zip');
        } catch (error) {
            // Don't show error for aborted requests
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }
            console.error(`Error downloading ${reportType} report:`, error);
            this.showToastNotification(
                'error',
                'Download Failed',
                `Unable to download ${reportType} report. Please try again.`
            );
        }
        finally {
            this.downloadingItems[itemName] = false;
        }
    }

    async downloadComplianceSummary(itemName: string, signal: AbortSignal) {
        if (!this.readyToLoadData()) return;

        this.downloadingItems[itemName] = true;
        try {
            const response = await networkOptimization.download_compliance_summary(
                this.runId!,
                this.revisionId,
                this.geographyFilters as IGeographyFilterPayload,
                this.distanceCalcType,
                signal
            );
            const {state, county} = this.appliedGeographyFilter;
            const level = state && county ? 'specialty' : state ? 'county' : 'state';
            download(response, `compliance-summary-${level}.csv`, 'text/csv');
        } catch (error) {
            // Don't show error for aborted requests
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }
            console.error('Error downloading compliance summary:', error);
            this.showToastNotification(
                'error',
                'Download Failed',
                'Unable to download compliance summary. Please try again.'
            );
        } finally {
            this.downloadingItems[itemName] = false;
        }
    }
}

